import { Quote } from '@client/model/quotesModel';
import { FundingPolicyType } from '@shared';
import { getMultiplierForPremium } from './lincolnPricingConfig';

const SECURIAN_MIN_DEATH_BENEFIT_CAP = 10000;
const SECURIAN_MIN_DEATH_BENEFIT_RATE = 0.1;

function calculateMinGuaranteedDeathBenefit(
  fundingPolicyType: number,
  scaledDeathBenefit: number,
  originalValue: number | undefined,
  scalingFactor: number,
): number | undefined {
  if (fundingPolicyType === FundingPolicyType.secureCareThree) {
    return Math.min(
      SECURIAN_MIN_DEATH_BENEFIT_CAP,
      scaledDeathBenefit * SECURIAN_MIN_DEATH_BENEFIT_RATE,
    );
  }
  return originalValue ? originalValue * scalingFactor : undefined;
}

function getScalingFactorForQuote(
  fundingPolicyType: number,
  originalPremium: number,
  newPremium: number,
): number {
  const linearFactor = newPremium / originalPremium;

  if (fundingPolicyType === FundingPolicyType.moneyGuardFixedAdvantage) {
    const originalMultiplier = getMultiplierForPremium(originalPremium);
    const newMultiplier = getMultiplierForPremium(newPremium);
    const correctionFactor = newMultiplier / originalMultiplier;
    return linearFactor * correctionFactor;
  }

  return linearFactor;
}

/**
 * Adjusts a quote's parameters based on a new premium amount.
 * MoneyGuard Fixed Advantage 2025 uses premium band correction for non-linear pricing
 * and applies a floor guarantee ensuring death benefit never falls below premium.
 * All other products use simple linear scaling.
 */
export const adjustQuoteForPremium = (originalQuote: Quote, newPremiumAmount: number): Quote => {
  if (!originalQuote.premiumAmount || originalQuote.premiumAmount <= 0) {
    return originalQuote;
  }

  const scalingFactor = getScalingFactorForQuote(
    originalQuote.fundingPolicyType,
    originalQuote.premiumAmount,
    newPremiumAmount,
  );
  let scaledDeathBenefit = originalQuote.deathBenefitInitial * scalingFactor;

  if (originalQuote.fundingPolicyType === FundingPolicyType.moneyGuardFixedAdvantage) {
    scaledDeathBenefit = Math.max(scaledDeathBenefit, newPremiumAmount);
  }

  const adjustedQuote: Quote = {
    ...originalQuote,
    premiumAmount: newPremiumAmount,

    deathBenefitInitial: scaledDeathBenefit,
    deathBenefitMinimumGuaranteedIndividual: calculateMinGuaranteedDeathBenefit(
      originalQuote.fundingPolicyType,
      scaledDeathBenefit,
      originalQuote.deathBenefitMinimumGuaranteedIndividual,
      scalingFactor,
    ),
    deathBenefitMinimumGuaranteedJoint: originalQuote.deathBenefitMinimumGuaranteedJoint
      ? originalQuote.deathBenefitMinimumGuaranteedJoint * scalingFactor
      : undefined,

    ltcBenefitMonthlyIndividual: originalQuote.ltcBenefitMonthlyIndividual * scalingFactor,
    ltcBenefitAnnualIndividual: originalQuote.ltcBenefitAnnualIndividual
      ? originalQuote.ltcBenefitAnnualIndividual * scalingFactor
      : undefined,
    ltcBenefitTotal: originalQuote.ltcBenefitTotal * scalingFactor,

    ltcBenefitDurationMonths: originalQuote.ltcBenefitDurationMonths,
    investmentMin: originalQuote.investmentMin,
    investmentMax: originalQuote.investmentMax,

    projectedCostCoverage: undefined,
    roi: undefined,
    roiValues: undefined,
    formattedROI: undefined,
  };

  return adjustedQuote;
};
export const adjustQuotesForPremium = (
  originalQuotes: Quote[],
  newPremiumAmount: number,
): Quote[] => originalQuotes.map(quote => adjustQuoteForPremium(quote, newPremiumAmount));

/**
 * Adjusts an EquiTrust quote's LTC benefits based on a new coverage ratio
 * LTC benefits scale proportionally with coverage ratio changes
 * Premium and death benefit remain unchanged
 */
export const adjustQuoteForCoverageRatio = (
  originalQuote: Quote,
  newCoverageRatio: number,
): Quote => {
  const originalCoverageRatio = originalQuote.coverageRatio;
  if (!originalCoverageRatio || originalCoverageRatio <= 0) {
    return originalQuote;
  }

  const minRatio = originalQuote.minCoverageRatio || originalCoverageRatio;
  const maxRatio = originalQuote.maxCoverageRatio || originalCoverageRatio;
  const clampedRatio = Math.max(minRatio, Math.min(maxRatio, newCoverageRatio));

  const scalingFactor = clampedRatio / originalCoverageRatio;

  return {
    ...originalQuote,
    coverageRatio: clampedRatio,
    ltcBenefitMonthlyIndividual: originalQuote.ltcBenefitMonthlyIndividual * scalingFactor,
    ltcBenefitAnnualIndividual: originalQuote.ltcBenefitAnnualIndividual
      ? originalQuote.ltcBenefitAnnualIndividual * scalingFactor
      : undefined,
    ltcBenefitTotal: originalQuote.ltcBenefitTotal * scalingFactor,
    projectedCostCoverage: undefined,
    roi: undefined,
    roiValues: undefined,
    formattedROI: undefined,
  };
};

